import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle } from 'lucide-react';
import FileUpload from './FileUpload';
import AttachmentsList from './AttachmentsList';
import { useAuth } from '../../../contexts/AuthContext';

const FileUploadManager = ({
  entityType, // 'repairs' or 'devices'
  entityId,
  disabled = false,
  className = '',
  compact = false
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Clear messages after a few seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const fetchAttachments = async () => {
    if (!entityId) return;
    
    if (!user) {
      console.log('User not authenticated, redirecting to login');
      navigate('/login');
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(`/api/attachments/${entityType}/${entityId}`);
      setAttachments(response.data);
    } catch (error) {
      console.error('Error fetching attachments:', error);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('Authentication failed, redirecting to login');
        navigate('/login');
      } else {
        setError('Failed to load attachments');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files) => {
    if (!entityId) {
      setError('Please save the record first before uploading files');
      return;
    }

    if (!user) {
      console.log('User not authenticated for upload');
      navigate('/login');
      return;
    }

    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      await axios.post(`/api/attachments/${entityType}/${entityId}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess(`${files.length} file(s) uploaded successfully`);
      await fetchAttachments(); // Refresh attachments list
    } catch (error) {
      console.error('Error uploading files:', error);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('Authentication failed during upload, redirecting to login');
        navigate('/login');
        return;
      }
      
      const errorMessage = error.response?.data?.error || 'Failed to upload files';
      setError(errorMessage);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await axios.delete(`/api/attachments/${entityType}/${entityId}/files/${attachmentId}`);
      setSuccess('Attachment deleted successfully');
      await fetchAttachments(); // Refresh attachments list
    } catch (error) {
      console.error('Error deleting attachment:', error);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('Authentication failed, redirecting to login');
        navigate('/login');
        return;
      }
      
      const errorMessage = error.response?.data?.error || 'Failed to delete attachment';
      setError(errorMessage);
    }
  };

  const handleDownloadAttachment = async (attachmentId, filename) => {
    try {
      // Create a direct download link using fetch with authentication headers
      const response = await axios.get(`/api/attachments/${entityType}/${entityId}/files/${attachmentId}`, {
        responseType: 'blob',
        headers: {
          'Authorization': axios.defaults.headers.common['Authorization']
        }
      });

      // Create blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/login');
        return;
      }
      
      setError('Failed to download file');
    }
  };

  // Fetch an image through the authenticated API and hand it back as a data: URL.
  // A plain <img src="/api/..."> can't work (authenticateToken only reads the
  // Authorization header), and a blob: URL is blocked by helmet's production CSP
  // (img-src 'self' data:) - so the bytes have to be inlined.
  const handlePreviewAttachment = async (attachmentId) => {
    const response = await axios.get(
      `/api/attachments/${entityType}/${entityId}/files/${attachmentId}`,
      { responseType: 'blob' }
    );

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(response.data);
    });
  };

  // Load attachments when component mounts or entityId changes
  useEffect(() => {
    fetchAttachments();
  }, [entityId, entityType]);

  if (!entityId) {
    return (
      <div className={`bg-blue-50 border border-blue-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-900">File Attachments</h4>
            <p className="text-sm text-blue-700 mt-1">
              Save the record first to upload files and attachments.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Success Message */}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md text-green-700">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">{success}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* File Upload */}
      <FileUpload
        onFilesSelected={() => {}} // We handle upload directly
        onUpload={handleFileUpload}
        maxFiles={10}
        maxFileSize={50}
        acceptMobileCapture={true}
        showPreview={true}
        disabled={disabled}
        compact={compact}
      />

      {/* Existing Attachments */}
      {loading ? (
        <div className="text-center py-4">
          <div className="inline-flex items-center gap-2 text-gray-500">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            Loading attachments...
          </div>
        </div>
      ) : (
        <AttachmentsList
          attachments={attachments}
          onDelete={handleDeleteAttachment}
          onDownload={handleDownloadAttachment}
          onPreview={handlePreviewAttachment}
          showActions={!disabled}
        />
      )}
    </div>
  );
};

export default FileUploadManager;
